import { useCallback, useEffect, useRef, useState } from 'react';
import { useOrgs } from '../../api/app-context';

/**
 * The nav rail's wordmark, and — for a reader in more than one org — the thing
 * that changes which one they are in.
 *
 * A member of one org sees exactly what was there before: the org name and
 * nothing to press. The switcher only exists where it means something, so the
 * common case carries no affordance it cannot use.
 *
 * There is no menu primitive in `components/ui`, and one disclosure does not
 * earn one: this composes what is already here — `CollapsedBin`'s
 * `aria-expanded` button and `Modal`'s window-level Escape.
 */
export function OrgSwitcher() {
  const { orgs, org, switchOrg } = useOrgs();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  const close = useCallback((focusTrigger: boolean) => {
    setOpen(false);
    // Escape and an outside click are different departures: the first is the
    // reader backing out of the menu and should leave them where they were.
    if (focusTrigger) trigger.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => {
      // `Modal` listens on window too: with a dialog open, Escape is its
      // dismissal, not ours.
      if (document.querySelector('[aria-modal="true"]')) return;
      if (event.key === 'Escape') close(true);
    };
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close(false);
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  if (orgs.length <= 1) {
    return (
      <div className="wordmark">
        <span className="org-name" title={org.name}>
          {org.name}
        </span>
      </div>
    );
  }

  return (
    <div className="org-switch" ref={root}>
      <button
        type="button"
        className="wordmark switch"
        ref={trigger}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="org-name" title={org.name}>
          {org.name}
        </span>
        <svg className="chev" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
          <path
            d="M3.5 4.75 6 7.25l2.5-2.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open ? (
        <div className="org-menu">
          {/* Plain buttons in a div, not ARIA menu roles: `role="menu"` promises
              arrow-key roving focus and takes Tab away from the items. Tab
              already walks them, and `aria-current` says which one they are in —
              that is the whole of what this disclosure owes a reader. */}
          {orgs.map((entry) => {
            const current = entry.id === org.id;
            return (
              <button
                key={entry.id}
                type="button"
                className={current ? 'org-item current' : 'org-item'}
                aria-current={current ? 'true' : undefined}
                onClick={() => {
                  setOpen(false);
                  // Picking the org they are already in is just a dismissal.
                  if (!current) switchOrg(entry.id);
                }}
              >
                {entry.name}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
